/**
 * MarCalendar.jsx
 *
 * DAILY MAR — Clean modern UI
 *
 * Status logic:
 * - GIVEN    → success (green)
 * - REFUSED  → error (red)
 * - MISSED   → warning (orange)
 * - PENDING  → warning (yellow)
 * - OVERDUE  → error (red)
 *
 * Rules:
 * - Previous days: all pending doses are overdue
 * - Today: overdue only if time has passed
 * - Future days: never overdue
 */

import React from "react";
import { Box, Paper, Typography, Chip, Button, Stack } from "@mui/material";

/* ============================================================
   Time utilities (AM / PM SAFE)
   ============================================================ */

/**
 * Converts time string to minutes since midnight
 * Supports: "8 AM", "12 PM", "9:30 AM", "14:00"
 */
const normalizeTimeToMinutes = (timeStr) => {
    if (!timeStr) return null;

    // AM / PM format
    const ampmMatch = timeStr
        .trim()
        .match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i);

    if (ampmMatch) {
        let hour = Number(ampmMatch[1]);
        const minute = Number(ampmMatch[2] || 0);
        const meridiem = ampmMatch[3].toUpperCase();

        if (meridiem === "AM") {
            if (hour === 12) hour = 0;
        } else {
            if (hour !== 12) hour += 12;
        }

        return hour * 60 + minute;
    }

    // 24-hour format
    const militaryMatch = timeStr.match(/^(\d{1,2}):(\d{2})$/);
    if (militaryMatch) {
        return Number(militaryMatch[1]) * 60 + Number(militaryMatch[2]);
    }

    return null;
};

const toLocalYmd = (dateLike) => {
    const raw = String(dateLike || "").trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
        // DATEONLY from API/DB should not be re-interpreted as UTC.
        return raw;
    }

    const d = new Date(dateLike);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};

const normalizeStatus = (value, signature = null) => {
    const raw = String(value || "").trim().toUpperCase();
    if (!raw) return "PENDING";
    if (raw === "TAKEN" || raw === "ADMINISTERED" || raw === "COMPLETED") return "GIVEN";
    if (raw === "GIVEN") return "GIVEN";
    if (raw === "MISSED") return "MISSED";
    if (raw === "REFUSED") return "REFUSED";
    if (raw === "PRN_GIVEN" || raw === "PRN") return "PRN_GIVEN";
    if (raw === "PRN_MISSED") return "PRN_MISSED";
    if (raw === "OVERRIDE_PENDING" || raw === "PENDING_OVERRIDE") {
        return signature ? "GIVEN" : "PENDING";
    }
    if (raw === "OVERDUE") return "OVERDUE";
    return raw;
};

const statusLabel = (value) => {
    const status = normalizeStatus(value);
    if (status === "GIVEN") return "Given";
    if (status === "MISSED") return "Missed";
    if (status === "REFUSED") return "Refused";
    if (status === "PRN_GIVEN") return "PRN Given";
    if (status === "PRN_MISSED") return "PRN Missed";
    if (status === "PENDING") return "Pending";
    return status;
};

const formatRecordedAt = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString([], {
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });
};

/* ============================================================
   Component
   ============================================================ */

export default function MarCalendar({ date, meds, doses, onLogDose }) {
    const day = toLocalYmd(date); // YYYY-MM-DD (local)

    const now = new Date();
    const todayDateString = toLocalYmd(now);

    /* ============================================================
       Build scheduled doses for selected day
       ============================================================ */

    const todaysDoses = meds.flatMap((med) => {
        if (!med.schedule?.times?.length) return [];

        return med.schedule.times.map((time) => {
            const scheduledTime = String(time || "").trim();
            const scheduledMinutes = normalizeTimeToMinutes(scheduledTime);
            const existing = doses.find(
                (d) => {
                    const sameMed = String(d.medId) === String(med.id);
                    const sameDate = toLocalYmd(d.date) === day;
                    if (!sameMed || !sameDate) return false;

                    const doseTime = String(d.timeOfDay || "").trim();
                    const doseMinutes = normalizeTimeToMinutes(doseTime);

                    if (scheduledMinutes != null && doseMinutes != null) {
                        return scheduledMinutes === doseMinutes;
                    }

                    return doseTime.toUpperCase() === scheduledTime.toUpperCase();
                }
            );

            return {
                medId: med.id,
                medName: med.name,
                dosage: med.dosage,
                timeOfDay: time,
                date: day,
                status: normalizeStatus(existing?.status || "PENDING", existing?.signature),
                signature: existing?.signature || null,
                recordedAt: existing?.recordedAt || null,
                _entryId: existing?.id || null,
                prn: med.schedule?.prn === true,
            };
        });
    });

    /* ============================================================
       Overdue logic (FIXED + SAFE)
       ============================================================ */

    const checkIsOverdue = (dose) => {
        // PRN is never auto-overdue
        if (dose.prn) return false;

        const timeMinutes = normalizeTimeToMinutes(dose.timeOfDay);
        if (timeMinutes == null) return false;

        const selectedDayDate = new Date(day + "T00:00:00");
        const todayDate = new Date(todayDateString + "T00:00:00");

        // Past days → always overdue
        if (selectedDayDate < todayDate) return true;

        // Future days → never overdue
        if (selectedDayDate > todayDate) return false;

        // Today → check time
        const scheduled = new Date(selectedDayDate);
        scheduled.setMinutes(timeMinutes);

        return now > scheduled;
    };

    /* ============================================================
       Chip color logic
       ============================================================ */

    const getChipColor = (dose) => {
        const status = normalizeStatus(dose.status);
        if (status === "GIVEN" || status === "PRN_GIVEN") return "success";
        if (status === "OVERDUE") return "error";
        if (status === "REFUSED") return "default";
        if (status === "MISSED" || status === "PRN_MISSED") return "warning";
        if (status === "PENDING") {
            return checkIsOverdue(dose) ? "error" : "warning";
        }
        return "default";
    };

    /* ============================================================
       UI
       ============================================================ */

    return (
        <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
                Daily MAR – {day}
            </Typography>

            {todaysDoses.map((dose, idx) => {
                const normalizedDoseStatus = normalizeStatus(dose.status);
                const isOverdue =
                    normalizedDoseStatus === "PENDING" && checkIsOverdue(dose);
                const isRefused = normalizedDoseStatus === "REFUSED";

                return (
                    <Box
                        key={idx}
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            p: 1.5,
                            mb: 1,
                            border: "1px solid #e5e7eb",
                            borderRadius: 1.5,
                            backgroundColor: "#fff",
                            "&:hover": { background: "#fafafa", borderColor: "#cbd5e1" },
                        }}
                    >
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 600 }}>
                                {dose.timeOfDay}
                            </Typography>

                            <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                {dose.medName}
                                {dose.dosage ? ` (${dose.dosage})` : ""}
                            </Typography>
                            {(dose.signature || dose.recordedAt) && (
                                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                    {dose.signature ? `Given by: ${dose.signature}` : ""}
                                    {dose.signature && dose.recordedAt ? " • " : ""}
                                    {dose.recordedAt ? formatRecordedAt(dose.recordedAt) : ""}
                                </Typography>
                            )}
                        </Box>

                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ pl: 1.5, ml: 1.5, borderLeft: "1px solid #e5e7eb" }}
                        >
                            <Chip
                                label={isOverdue ? "Overdue" : statusLabel(normalizedDoseStatus)}
                                color={getChipColor(dose)}
                                variant={isRefused ? "outlined" : "filled"}
                                sx={{
                                    minWidth: 90,
                                    textAlign: "center",
                                    fontWeight: 600,
                                    ...(isRefused
                                        ? {
                                            color: "#b45309",
                                            borderColor: "#b45309",
                                            backgroundColor: "#fff7ed",
                                        }
                                        : {}),
                                }}
                            />
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => onLogDose(dose)}
                            >
                                Update MAR Status
                            </Button>
                        </Stack>
                    </Box>
                );
            })}
        </Paper>
    );
}
